import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchLotFunctionalUnits } from '../../services/lotsService';

/**
 * Clave de cache: lote + owner del espacio. En espacio ajeno el `user_lot_id`
 * de cada UF es del owner, así que cambiar de espacio no reusa la respuesta.
 *
 * @param {string|null|undefined} lotId
 * @param {string|null|undefined} ownerUserId
 * @returns {string|null}
 */
export function buildLotFunctionalUnitsCacheKey(lotId, ownerUserId = null) {
  if (!lotId) return null;
  const owner = String(ownerUserId || '').trim();
  return `${lotId}|${owner}`;
}

/**
 * UF catastrales de Córdoba. Lazy respecto del GET del lote: se pide al abrir
 * el tab Unidades (un PH puede tener 142 UF).
 *
 * Si el usuario sale del tab, se conserva el cache del mismo lote + owner para
 * no volver a pedir 142 UF al reabrir. Al cambiar de parcela o de espacio se limpia.
 *
 * @param {object} options
 * @param {string|null|undefined} options.lotId
 * @param {boolean} [options.enabled=false]
 * @param {string|null} [options.ownerUserId=null] - Espacio ajeno → `?owner_user_id=`.
 * @returns {{ officialUnits: object[], officialUnitsLoading: boolean, officialUnitsErrorStatus: number|null, reloadOfficialUnits: function }}
 */
export function useLotFunctionalUnits({ lotId, enabled = false, ownerUserId = null }) {
  const [officialUnits, setOfficialUnits] = useState([]);
  const [officialUnitsLoading, setOfficialUnitsLoading] = useState(false);
  const [officialUnitsErrorStatus, setOfficialUnitsErrorStatus] = useState(null);
  const cacheKey = buildLotFunctionalUnitsCacheKey(lotId, ownerUserId);
  const cachedKeyRef = useRef(null);
  // Clave vigente: descarta respuestas de un lote / espacio anterior (reload sin abort).
  const currentKeyRef = useRef(cacheKey);
  currentKeyRef.current = cacheKey;

  const fetchUnits = useCallback(async (signal, { silent = false } = {}) => {
    if (!lotId) {
      setOfficialUnits([]);
      cachedKeyRef.current = null;
      return [];
    }
    const requestKey = cacheKey;
    if (!silent) setOfficialUnitsLoading(true);
    try {
      const { units, errorStatus } = await fetchLotFunctionalUnits(lotId, { signal, ownerUserId });
      if (currentKeyRef.current !== requestKey) return [];
      setOfficialUnits(units);
      setOfficialUnitsErrorStatus(errorStatus);
      cachedKeyRef.current = errorStatus ? null : requestKey;
      return units;
    } catch (error) {
      if (error?.name === 'AbortError') return [];
      console.error('Error loading lot functional units:', error);
      if (!silent && currentKeyRef.current === requestKey) setOfficialUnits([]);
      return [];
    } finally {
      if (!silent && currentKeyRef.current === requestKey) setOfficialUnitsLoading(false);
    }
  }, [lotId, ownerUserId, cacheKey]);

  useEffect(() => {
    if (cachedKeyRef.current && cachedKeyRef.current !== cacheKey) {
      setOfficialUnits([]);
      setOfficialUnitsErrorStatus(null);
      cachedKeyRef.current = null;
    }

    if (!lotId) {
      setOfficialUnits([]);
      setOfficialUnitsErrorStatus(null);
      cachedKeyRef.current = null;
      setOfficialUnitsLoading(false);
      return undefined;
    }

    if (!enabled) {
      setOfficialUnitsLoading(false);
      return undefined;
    }

    if (cachedKeyRef.current === cacheKey) {
      return undefined;
    }

    // Lote / espacio nuevo sin cache: no mostrar las UF del anterior mientras carga.
    setOfficialUnits([]);
    setOfficialUnitsErrorStatus(null);
    const ac = new AbortController();
    fetchUnits(ac.signal, { silent: false });
    return () => ac.abort();
  }, [lotId, enabled, cacheKey, fetchUnits]);

  const reloadOfficialUnits = useCallback(() => {
    if (!enabled || !lotId) return Promise.resolve([]);
    return fetchUnits(undefined, { silent: true });
  }, [enabled, lotId, fetchUnits]);

  return {
    officialUnits,
    officialUnitsLoading,
    officialUnitsErrorStatus,
    reloadOfficialUnits,
  };
}
